import { gql } from '@apollo/client';

import axios, { endpoints } from 'src/lib/axios';
import { apolloClient } from 'src/lib/apollo-client';
import { OAUTH_CONFIG, type IdentityProvider } from 'src/config/oauth';

import { setSession } from './utils';

// ----------------------------------------------------------------------

const AUTH_LOGIN_MUTATION = gql`
  mutation AuthLogin($email: String!, $password: String!) {
    authLogin(email: $email, password: $password) {
      token
      refreshToken
      user {
        id
        username
        email
        firstName
        lastName
        roles
      }
    }
  }
`;

const REGISTER_USER_MUTATION = gql`
  mutation RegisterUser($email: String!, $password: String!) {
    registerUser(email: $email, password: $password) {
      token
      refreshToken
      user {
        id
        username
        email
        firstName
        lastName
        roles
      }
    }
  }
`;

const REFRESH_TOKEN_MUTATION = gql`
  mutation RefreshToken {
    refreshToken {
      token
      refreshToken
      user {
        id
        username
        email
        firstName
        lastName
        roles
      }
    }
  }
`;

type AuthLoginPayload = {
  token: string;
  refreshToken?: string | null;
  user: Record<string, unknown> | null;
};

type AuthLoginResponse = {
  authLogin?: AuthLoginPayload | null;
};

type RegisterUserResponse = {
  registerUser?: AuthLoginPayload | null;
};

type RefreshTokenResponse = {
  refreshToken?: AuthLoginPayload | null;
};

export type SignInParams = {
  email: string;
  password: string;
};

export type SignUpParams = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
};

/** **************************************
 * Sign in
 *************************************** */
export const signInWithPassword = async ({
  email,
  password,
}: SignInParams): Promise<AuthLoginPayload> => {
  try {
    const { data } = await apolloClient.mutate<AuthLoginResponse, SignInParams>({
      mutation: AUTH_LOGIN_MUTATION,
      variables: { email, password },
      fetchPolicy: 'no-cache',
    });

    const payload = data?.authLogin;

    if (!payload?.token) {
      throw new Error('auth.login.unknownResponse');
    }

    await setSession(payload.token, async () => {
      await refreshAccessToken();
    });

    return payload;
  } catch (error: any) {
    console.error('Error during sign in:', error);

    if (error && error.graphQLErrors.length > 0) {
      throw new Error(error.graphQLErrors[0]?.message ?? 'auth.login.failed');
    }

    throw error;
  }
};

/** **************************************
 * Sign up (legacy)
 *************************************** */
export const signUp = async ({
  email,
  password,
  firstName,
  lastName,
}: SignUpParams): Promise<void> => {
  const params = {
    email,
    password,
    firstName,
    lastName,
  };

  try {
    const res = await axios.post(endpoints.auth.signUp, params);

    const { accessToken } = res.data;

    if (!accessToken) {
      throw new Error('Access token not found in response');
    }

    await setSession(accessToken);
  } catch (error) {
    console.error('Error during sign up:', error);
    throw error;
  }
};

/** **************************************
 * Register user
 *************************************** */
export const registerUser = async ({
  email,
  password,
}: SignInParams): Promise<AuthLoginPayload> => {
  try {
    const { data } = await apolloClient.mutate<RegisterUserResponse, SignInParams>({
      mutation: REGISTER_USER_MUTATION,
      variables: { email, password },
      fetchPolicy: 'no-cache',
    });

    const payload = data?.registerUser;

    if (!payload?.token) {
      throw new Error('auth.register.unknownResponse');
    }

    await setSession(payload.token, async () => {
      await refreshAccessToken();
    });

    return payload;
  } catch (error: any) {
    console.error('Error during registration:', error);

    if (error && error.graphQLErrors.length > 0) {
      throw new Error(error.graphQLErrors[0]?.message ?? 'auth.register.failed');
    }

    throw error;
  }
};

/** **************************************
 * Sign out
 *************************************** */
export const signOut = async (): Promise<void> => {
  try {
    await setSession(null);
    // Clear Apollo Client cache
    await apolloClient.clearStore();
  } catch (error) {
    console.error('Error during sign out:', error);
    throw error;
  }
};

/** **************************************
 * Refresh token
 *************************************** */
export const refreshAccessToken = async (): Promise<AuthLoginPayload> => {
  try {
    const { data } = await apolloClient.mutate<RefreshTokenResponse>({
      mutation: REFRESH_TOKEN_MUTATION,
      fetchPolicy: 'no-cache',
    });

    const payload = data?.refreshToken;

    if (!payload?.token) {
      throw new Error('auth.refresh.unknownResponse');
    }

    // When refreshing, pass the refresh callback to continue the cycle
    await setSession(payload.token, async () => {
      await refreshAccessToken();
    });

    return payload;
  } catch (error: any) {
    console.error('Error during token refresh:', error);

    if (error && error.graphQLErrors?.length > 0) {
      throw new Error(error.graphQLErrors[0]?.message ?? 'auth.refresh.failed');
    }

    throw error;
  }
};

/** **************************************
 * OAuth - Initiate sign in with identity provider
 *************************************** */
export const initiateOAuthSignIn = (provider: IdentityProvider): void => {
  const idpHint = OAUTH_CONFIG.IDENTITY_PROVIDERS[provider];
  const callbackUrl = `${OAUTH_CONFIG.CLIENT_CALLBACK_URL}/auth/callback`;

  const authUrl = new URL(`${OAUTH_CONFIG.FUSIONAUTH_URL}/oauth2/authorize`);
  authUrl.searchParams.set('client_id', OAUTH_CONFIG.APPLICATION_ID);
  authUrl.searchParams.set('response_type', 'code');
  authUrl.searchParams.set('redirect_uri', callbackUrl);
  authUrl.searchParams.set('idp_hint', idpHint);

  // Redirect to FusionAuth OAuth authorization endpoint
  window.location.href = authUrl.toString();
};

/** **************************************
 * OAuth - Handle callback and exchange code for token
 *************************************** */
export const handleOAuthCallback = async (code: string): Promise<AuthLoginPayload> => {
  try {
    const callbackUrl = `${OAUTH_CONFIG.CLIENT_CALLBACK_URL}/auth/callback`;

    const response = await axios.post<{
      success: boolean;
      token: string;
      refreshToken?: string;
      user: Record<string, unknown>;
    }>('/api/v1/auth/oauth/callback', {
      code,
      redirect_uri: callbackUrl,
    });

    const { token, refreshToken, user } = response.data;

    if (!token) {
      throw new Error('auth.oauth.noToken');
    }

    await setSession(token, async () => {
      await refreshAccessToken();
    });

    return {
      token,
      refreshToken: refreshToken || null,
      user,
    };
  } catch (error: any) {
    console.error('Error during OAuth callback:', error);

    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }

    throw new Error('auth.oauth.callbackFailed');
  }
};

/** **************************************
 * OAuth - Convenience functions for specific providers
 *************************************** */
export const signInWithGoogle = (): void => initiateOAuthSignIn('GOOGLE');
export const signInWithGithub = (): void => initiateOAuthSignIn('GITHUB');
export const signInWithApple = (): void => initiateOAuthSignIn('APPLE');
export const signInWithMicrosoft = (): void => initiateOAuthSignIn('MICROSOFT');
export const signInWithEduplaces = (): void => initiateOAuthSignIn('EDUPLACES');
